import { useMemo } from "react";
import {
  Box,
  Flex,
  IconButton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { PencilSimple, Trash } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { formatKwh } from "./helpers";
import {
  activeDimensionCount,
  renderDimensionHeaderCells,
  renderDimensionValueCells,
} from "./dimensionDisplay";
import { useV2TableFilters } from "./useV2TableFilters";
import DimensionsGridFilterBar from "../DimensionsGrid/DimensionsGridFilterBar";
import { Paginator } from "../../../../components/Paginator";

const TH_PROPS = {
  py: 3,
  px: 4,
  fontSize: "13px",
  color: "gray.600",
  fontWeight: "600",
  textTransform: "none",
  letterSpacing: "normal",
};

/**
 * Wide read display for on-site generation. Distinct schema from the
 * purchased-electricity layout: Fuel/Tech, Total Generated, Self Consumed,
 * Exported, plus four certificate-related columns. Wide enough that the table
 * scrolls horizontally. Edit/delete hidden in preview mode.
 *
 * `rows` are raw API records (dimensionItems nested, fuelType/certificate
 * fields at top level, certificatesIssued a boolean). A shared hook
 * flattens/decorates them and provides the dimension + fuel/certificate filter
 * bar and client-side pagination (matching the Scope 1/3 task tables).
 */
function OnsiteGenerationTable({ organizationStructure, rows, isPreview, onEdit, onDelete }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope2V2",
  });

  const dimCount = activeDimensionCount(organizationStructure);

  const filterColumns = useMemo(
    () => [
      {
        key: "fuelType",
        label: t("columns.fuelType"),
        getValue: (r) => (r.fuelType ? t(`fuelTypes.${r.fuelType}`) : ""),
      },
      {
        key: "certificateType",
        label: t("columns.certificateType"),
        getValue: (r) => (r.certificateType ? t(`certificateTypes.${r.certificateType}`) : ""),
      },
    ],
    [t]
  );

  const {
    dimensionGroups,
    extraFilterColumns,
    filters,
    handleFilterChange,
    clearAllFilters,
    filteredItems,
    pageSlice,
    totalPages,
    safePageIndex,
    pageSize,
    handlePageSelection,
    handlePageSizeChange,
    totalCount,
  } = useV2TableFilters(organizationStructure, rows, filterColumns);

  // [dimensions...] fuel + 3 kWh + issued + certType + 2 cert kWh + actions
  const totalColumns = dimCount + 9;

  return (
    <>
      {rows.length > 0 && (
        <DimensionsGridFilterBar
          dimensionGroups={dimensionGroups}
          columns={extraFilterColumns}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearAll={clearAllFilters}
          filteredCount={filteredItems.length}
          totalCount={totalCount}
        />
      )}
      <Box border="1px solid" borderColor="gray.200" borderRadius="md" bg="white" mb={2} overflowX="auto">
        <Table size="sm" variant="unstyled" minW="1200px">
          <Thead>
            <Tr bg="gray.50">
              {renderDimensionHeaderCells(organizationStructure)}
              <Th {...TH_PROPS}>{t("columns.fuelType")}</Th>
              <Th {...TH_PROPS} textAlign="right">{t("columns.totalGenerated")}</Th>
              <Th {...TH_PROPS} textAlign="right">{t("columns.selfConsumed")}</Th>
              <Th {...TH_PROPS} textAlign="right">{t("columns.exportedToGrid")}</Th>
              <Th {...TH_PROPS}>{t("columns.certificatesIssued")}</Th>
              <Th {...TH_PROPS}>{t("columns.certificateType")}</Th>
              <Th {...TH_PROPS} textAlign="right">{t("columns.certificatesRetained")}</Th>
              <Th {...TH_PROPS} textAlign="right">{t("columns.certificatesTransferred")}</Th>
              <Th py={3} px={4} width="80px" />
            </Tr>
          </Thead>
          <Tbody>
            {pageSlice.map((row, idx) => {
              const record = row.__record;
              const issued = row.certificatesIssued === true || row.certificatesIssued === "yes";
              const certLabel = row.certificateType ? t(`certificateTypes.${row.certificateType}`) : "";
              return (
                <Tr key={row.id} borderTop={idx === 0 ? "none" : "1px solid"} borderColor="gray.100">
                  {renderDimensionValueCells(organizationStructure, row)}
                  <Td py={3} px={4} fontSize="14px" color="gray.700">
                    {row.fuelType ? t(`fuelTypes.${row.fuelType}`) : ""}
                  </Td>
                  <Td py={3} px={4} fontSize="14px" color="gray.700" textAlign="right">{formatKwh(row.totalGenerated)}</Td>
                  <Td py={3} px={4} fontSize="14px" color="gray.700" textAlign="right">{formatKwh(row.selfConsumed)}</Td>
                  <Td py={3} px={4} fontSize="14px" color="gray.700" textAlign="right">{formatKwh(row.exportedToGrid)}</Td>
                  <Td py={3} px={4} fontSize="14px" color={issued ? "gray.700" : "gray.400"}>
                    {issued ? "Yes" : "No"}
                  </Td>
                  <Td
                    py={3}
                    px={4}
                    fontSize="14px"
                    color={issued ? (certLabel ? "gray.700" : "red.500") : "gray.400"}
                  >
                    {issued ? certLabel || t("required") : "—"}
                  </Td>
                  <Td py={3} px={4} fontSize="14px" color={issued ? "gray.700" : "gray.400"} textAlign="right">
                    {issued ? formatKwh(row.certificatesRetained) : "—"}
                  </Td>
                  <Td py={3} px={4} fontSize="14px" color={issued ? "gray.700" : "gray.400"} textAlign="right">
                    {issued ? formatKwh(row.certificatesTransferred) : "—"}
                  </Td>
                  <Td py={3} px={4} textAlign="right">
                    {!isPreview && (
                      <Flex justify="flex-end" gap={0}>
                        <IconButton
                          aria-label="Edit row"
                          icon={<PencilSimple size={14} />}
                          size="sm"
                          variant="ghost"
                          color="gray.500"
                          _hover={{ color: "limeShade.600", bg: "lightLime" }}
                          onClick={() => onEdit(record)}
                        />
                        <IconButton
                          aria-label="Delete row"
                          icon={<Trash size={14} />}
                          size="sm"
                          variant="ghost"
                          color="gray.500"
                          _hover={{ color: "red.500", bg: "red.50" }}
                          onClick={() => onDelete(record)}
                        />
                      </Flex>
                    )}
                  </Td>
                </Tr>
              );
            })}
            {filteredItems.length === 0 && (
              <Tr>
                <Td colSpan={totalColumns} py={6} px={4} textAlign="center">
                  <Text fontSize="14px" color="gray.400">{t("noItemsMatchFilters")}</Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
      {filteredItems.length > 0 && (
        <Flex justifyContent={isPreview ? "center" : "start"} mb={2}>
          <Paginator
            selectedPage={safePageIndex + 1}
            numberOfPages={totalPages}
            handlePageSelection={handlePageSelection}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            totalCount={filteredItems.length}
          />
        </Flex>
      )}
    </>
  );
}

export default OnsiteGenerationTable;
